// Shared cover/entity image upload for the staff forms. Presentational +
// controlled: the orchestrator owns the File handle + the resolved preview URL.
// The 5MB client-side guard matches MAX_IMAGE_BYTES on the backend (ADR-0007).
// One copy for zone / exhibit / exhibition — `label` is the only per-owner
// difference (review #8; replaced the byte-identical zones/ + exhibits/ pair).

import type { FC, ChangeEvent } from "react";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export interface ImageUploadProps {
  label: string;
  previewUrl?: string | null;
  onFileSelected: (file: File) => void;
  onFileTooLarge?: () => void;
}

const ImageUpload: FC<ImageUploadProps> = ({
  label,
  previewUrl,
  onFileSelected,
  onFileTooLarge,
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onFileTooLarge?.();
      return;
    }
    onFileSelected(file);
  };

  return (
    <div className="space-y-4" data-testid="image-upload-section">
      <Label>{label}</Label>
      <div className="flex flex-col gap-4">
        {previewUrl && (
          <div className="w-full max-w-md">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-64 object-cover rounded-lg border border-border"
              data-testid="image-preview"
            />
          </div>
        )}
        <div>
          <Input
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleChange}
            data-testid="image-upload-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            JPG or PNG, max 5MB
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
